import type { Scene } from 'phaser';
import { MidiEventState } from 'Types';
import { Timestamp } from 'Types/ExerciseData';
import type { PlaybackPhase } from 'Phaser/PlaybackMachine/playbackMachine.types';

export type StartPlayingArgs = {
  measureOffset: number;
  processEventsQueue: [() => void, unknown];
};

export type AdaptiveTempoState = {
  state: 'started' | 'completed' | 'progress';
  value: number;
  isBottomPhrase?: boolean;
};

export enum AppTourStepHelper {
  ExpandTempoControls = 'expand-tempo-controls',
  EndTour = 'end-tour',
}

export type ToReactEvents = {
  ObjectiveComplete: { jumpTo: Timestamp };
  ExercisePassed: void;
  ExercisePaused: { lastPlayedMeasure?: number; pausedAtTimestamp?: number };
  ExerciseResumed: void;
  PhaserReady: void;
  CurrentSceneReady: Scene;
  ShowExercise: void;
  ObjectiveSkipped: { jumpTo: Timestamp; isLastObjective: boolean };
  HideExercise: void;
  SkipButtonRequest: void;
  StartedPlaying: StartPlayingArgs | void;
  ShowToast: string;
  HideToast: void;
  SetPhrasesTillNextTier: { phrasesTillNextTier: number };
  SetPhraseEligible: { eligible: boolean };
  ShowFeedbackToast: { state: MidiEventState };
  RepertoireCompleted: void;
  RepertoirePhraseCompleted: { phraseIndex: number };
  /** Emitted only after the entire transactional OSMD phrase batch has
   * rendered, laid out, and activated. React keeps its loading state visible
   * until this arrives. */
  RepertoireScoreReady: { loadId?: string; phraseCount: number };
  SetNotePosDummy: { x: number; y: number };
  SetAdaptiveTempoState: AdaptiveTempoState;
  SuggestLevelChange: { performance: 'good' | 'poor'; customGoal?: boolean };
  AdaptiveTempoRegionCreated: 'speed-up' | 'slow-down';
  UpdateTempo: { tempo: number };
  /** Legacy: emitted by NoteSequenceExercise when exercise ends. */
  ExerciseEnded: void;
  /** Legacy: emitted by NoteSequenceExercise on fail. */
  ExerciseFail: void;
  AppTourHelper: { step: AppTourStepHelper };
  SetLoopBounds: { startTimestamp: number; endTimestamp: number } | null;
  /** MUS-243: emitted by the scene's playback machine on every state
   *  transition. Container listens and mirrors into Redux `playbackPhase`.
   *  `phase` reuses the shared {@link PlaybackPhase} type so this contract
   *  stays in sync with the machine's state names. */
  PlaybackPhaseChanged: {
    phase: PlaybackPhase;
    isLoopingActive: boolean;
    hasEverPlayedInThisLoop: boolean;
  };
  /** `sequenceId` is a monotonic per-request id assigned by the PhraseManager.
   *  React echoes it back on the matching PhraseReady so the manager can restore
   *  request order for the initial fill (responses may resolve out of order). */
  FetchPhrase: { excludeFirstNoteRest: boolean; sequenceId: number };
  /** Perfect Phrase Counter: the user just completed the qualifying streak of
   *  perfect loops, so the looped section is "perfected". The container shows a
   *  bottom toast in response. */
  SectionPerfected: void;
};
